import React, { useState, useEffect } from "react";
import styled from "styled-components";
import SidebarData from "./SidebarData";
import SubMenu from "./SubMenu";
import { IconContext } from "react-icons/lib";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, IconButton, Button, Box, Typography } from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Badge from "@mui/material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import config from "../config";
import logo from "../../assets/PhamEvo_Logo.jpg"
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Nav = styled.div`
    background: #001f3f;
    height: 55px;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    padding-left: 250px; /* Adjust to match sidebar width */
`;

const SidebarNav = styled.nav`
    background: #001f3f;
    width: 250px;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1001;
    display: flex;
    justify-content: center;
`;

const SidebarWrap = styled.div`
    width: 100%;
`;

const UserProfile = styled.div`
    display: flex;
    align-items: center;
    color: white;
    cursor: pointer;
    position: relative;
`;

const ProfileText = styled.span`
    margin-left: 10px;
    font-weight: bold;
`;

const Sidebar = ({ setIsAuthenticated }) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [employeeName, setEmployeeName] = useState("Loading...");
    const [accessLevel, setAccessLevel] = useState(null);
    const [openNotificationModal, setOpenNotificationModal] = useState(false);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [equipmentPendingRequests, setEquipmentPendingRequests] = useState([]);

    const [notificationCount, setNotificationCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    useEffect(() => {
        try {
            const rawAccessLevel = localStorage.getItem("Boardroom_AccessLevel");
            const accessLevel = rawAccessLevel ? rawAccessLevel.replace(/"/g, '') : null;
            const storedEmpCode = localStorage.getItem("Empcode");
            if (accessLevel) {
                // console.log("Access Level:", rawAccessLevel);
                setAccessLevel(accessLevel);
            } else {
                console.warn("Invalid AccessLevel format in localStorage.");
            }
        } catch (error) {
            console.error("Error parsing localStorage data:", error);
        }
    }, []);

    const fetchPendingRequests = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const response = await axios.get(`${config.API_BASE_URL}/boardroomReservation/getPendingRequests`);
            const pendingList = Array.isArray(response.data) ? response.data : [];
            setPendingRequests(pendingList);
            setNotificationCount(pendingList.length);
        } catch (error) {
            console.error("Error fetching pending requests:", error);
            setPendingRequests([]);
            setNotificationCount(0);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingRequestForEquipments = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const response = await axios.get(`${config.API_BASE_URL}/equipmentReservation/getPendingRequests`);

            const pendingList = Array.isArray(response.data) ? response.data : [];
            setEquipmentPendingRequests(pendingList);
            setNotificationCount(pendingList.length);
        } catch (error) {
            console.error("Error fetching pending equipment requests:", error);
            setPendingRequests([]);
            setNotificationCount(0);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const loadRequests = () => {
            if (accessLevel === "ADMIN") {
                fetchPendingRequests();
            } else if (accessLevel === "IT_HELPDESK") {
                fetchPendingRequestForEquipments();
            }
        };

        loadRequests(); 
        return () => {
        };
    }, [accessLevel]); 



    const handleChangeRequest = async (reservationId, action) => {
        setLoading(true);
        try {
            const response = await axios.put(`${config.API_BASE_URL}/boardroomReservation/approveChange/${reservationId}`, { action });
            setOpenSnackbar(true);
            if (action === "approved") {
                const updatedRequests = pendingRequests.map((request) => {
                    if (request.reservationId !== reservationId) return request;

                    const updatedRequest = { ...request, status: "active" };

                    // Only update if the requested field exists (non-null or non-undefined)
                    if (request.requestedRoomName) updatedRequest.roomName = request.requestedRoomName;
                    if (request.requestedSelectedStartDate) updatedRequest.selectedStartDate = request.requestedSelectedStartDate;
                    if (request.requestedSelectedEndDate) updatedRequest.selectedEndDate = request.requestedSelectedEndDate;
                    if (request.requestedSelectedLocation) updatedRequest.selectedLocation = request.requestedSelectedLocation;
                    if (request.requestedNoOfPersons !== null && request.requestedNoOfPersons !== undefined)
                        updatedRequest.noOfPersons = request.requestedNoOfPersons;
                    if (request.requestedPurposeOfMeeting) updatedRequest.purposeOfMeeting = request.requestedPurposeOfMeeting;

                    // Clear requested fields
                    updatedRequest.requestedRoomName = null;
                    updatedRequest.requestedSelectedStartDate = null;
                    updatedRequest.requestedSelectedEndDate = null;
                    updatedRequest.requestedSelectedLocation = null;
                    updatedRequest.requestedNoOfPersons = null;
                    updatedRequest.requestedPurposeOfMeeting = null;

                    return updatedRequest;
                });

                setPendingRequests(updatedRequests);

                if (accessLevel === "ADMIN") {
                    fetchPendingRequests();
                }
            } else {
                // Rejected: remove from state
                setPendingRequests((prevRequests) =>
                    prevRequests.filter((request) => request.reservationId !== reservationId)
                );
            }
        } catch (error) {
            console.error("Error processing request:", error);
            console.error("Error details:", error.response || error.message);
            toast.error("Error processing request");
        } finally {
            setLoading(false);
        }
    };


    const handleChangeEquipmentRequest = async (equipmentReservationId, action) => {
        setLoading(true);
        try {
            const response = await axios.put(`${config.API_BASE_URL}/equipmentReservation/approveChange/${equipmentReservationId}`, { action });
            setOpenSnackbar(true);

            if (action === "approved") {
                const updatedRequests = pendingRequests.map((request) => {
                    if (request.equipmentReservationId !== equipmentReservationId) return request;

                const updatedRequest = { ...request, status: "active" };

                // Only update if the requested field exists (non-null or non-undefined)
                if (request.requestedEquipmentName) updatedRequest.equipmentName = request.requestedEquipmentName;
                if (request.requestedSelectedStartDate) updatedRequest.selectedStartDate = request.requestedSelectedStartDate;
                if (request.requestedSelectedEndDate) updatedRequest.selectedEndDate = request.requestedSelectedEndDate;
                if (request.requestedSelectedLocation) updatedRequest.selectedLocation = request.requestedSelectedLocation;
                if (request.requestedPurposeOfEquipment) updatedRequest.purposeOfEquipment = request.requestedPurposeOfEquipment;

                // Clear requested fields
                updatedRequest.requestedEquipmentName = null;
                updatedRequest.requestedSelectedStartDate = null;
                updatedRequest.requestedSelectedEndDate = null;
                updatedRequest.requestedSelectedLocation = null;
                updatedRequest.requestedPurposeOfEquipment = null;

                return updatedRequest;
            });

            setPendingRequests(updatedRequests);

            if (accessLevel === "IT_HELPDESK") {
                fetchPendingRequestForEquipments();
            }
            } else {
                setEquipmentPendingRequests((prevRequests) =>
                    prevRequests.filter((request) => request.equipmentReservationId !== equipmentReservationId)
                );
            }
        } catch (error) {
            console.error("Error processing request:", error);
            console.error("Error details:", error.response || error.message);
            toast.error("Error processing request");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const fetchUser = async () => {
            const EmpCode = localStorage.getItem("Empcode");
            if (!EmpCode) {
                setEmployeeName("Guest");
                return;
            }

            try {
                const response = await axios.get(`${config.API_BASE_URL}/getUsers/${EmpCode}`);
                const userData = response.data;
                setEmployeeName(`${userData.EmployeeName}`);
            } catch (error) {
                console.error("Error fetching user data:", error);
                setEmployeeName("Unknown User");
            }
        };

        fetchUser();
    }, []);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        localStorage.removeItem("userLoggedIn");
        localStorage.removeItem("Empcode");
        localStorage.removeItem("Boardroom_AccessLevel");
        setIsAuthenticated(false);
        navigate("/");
    };

    const handleMyAccount = () => {
        navigate("/my-account");
    };

    const handleOpenNotifications = () => setOpenNotificationModal(true);
    const handleCloseNotifications = () => setOpenNotificationModal(false);

    const sidebarItems = SidebarData(accessLevel);
    return (
        <>
            <IconContext.Provider value={{ color: "#fff" }}>
                <Nav>
                    <h3 style={{ color: "white", marginLeft: "5px", marginTop: "5px" }} className="nav-item">
                        Online Reservation System
                    </h3>

                    <div style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
                        {(accessLevel === "ADMIN" || accessLevel === "IT_HELPDESK") && (
                            <Badge
                                badgeContent={notificationCount}
                                color="error"
                                overlap="circular"
                                anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                            >
                                <NotificationsIcon
                                    fontSize="large"
                                    style={{ color: "white", cursor: "pointer", marginRight: "20px" }}
                                    onClick={handleOpenNotifications}
                                />
                            </Badge>

                        )}

                        <UserProfile onClick={handleMenuOpen}>
                            <AccountCircleIcon fontSize="large" />
                            <ProfileText>{employeeName}</ProfileText>
                            <ArrowDropDownIcon />
                        </UserProfile>
                    </div>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        style={{ marginLeft: "60px" }}
                    >
                        <MenuItem onClick={handleMyAccount}>
                            <PersonIcon style={{ marginRight: 8 }} /> My Account
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>
                            <LogoutIcon style={{ marginRight: 8 }} /> Logout
                        </MenuItem>
                    </Menu>
                </Nav>

                <SidebarNav>
                    <SidebarWrap>
                        <div style={{ marginLeft: "15px" }}>
                            <a
                                href="http://br.pharmevo.biz/"
                                className="brand-link bg-navy text-sm"
                            >
                                <img
                                    src={logo}
                                    alt="Store Logo"
                                    className="brand-image elevation-3"
                                    style={{
                                        marginTop: "10px",
                                        width: "12rem",
                                        height: "4.5rem",
                                        borderRaius: "10px",
                                    }}
                                />
                            </a>
                        </div>

                        <hr style={{ color: "white" }} />

                        {sidebarItems.map((item, index) => (
                            <SubMenu item={item} key={index} />
                        ))}
                    </SidebarWrap>
                </SidebarNav>

                <Dialog open={openNotificationModal} onClose={handleCloseNotifications} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        Notifications
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseNotifications}
                            style={{ position: "absolute", right: 10, top: 10 }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <hr />
                    </DialogTitle>
                    <DialogContent>
                        <List>
                            {accessLevel === "ADMIN" && (
                                pendingRequests.length > 0 ? (
                                    pendingRequests.map((request) => (
                                        <ListItem button key={request.reservationId}>
                                            <Box sx={{ width: "100%" }}>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {request.reserveBy} Request to Change
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Room Name: {request.requestedRoomName}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Requested by: {request.reserveBy}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Start: {new Date(request.requestedSelectedStartDate).toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    End: {new Date(request.requestedSelectedEndDate).toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    No of Persons: {request.requestedNoOfPersons}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Purpose of Meeting: {request.requestedPurposeOfMeeting}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => handleChangeRequest(request.reservationId, "approved")}
                                                    disabled={loading}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleChangeRequest(request.reservationId, "rejected")}
                                                    disabled={loading}
                                                >
                                                    Reject
                                                </Button>
                                            </Box>
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>
                                        <ListItemText primary="No pending requests." />
                                    </ListItem>
                                )
                            )}

                            {accessLevel === "IT_HELPDESK" && (
                                equipmentPendingRequests.length > 0 ? (
                                    equipmentPendingRequests.map((request) => (
                                        <ListItem button key={request.equipmentReservationId}>
                                            <Box sx={{ width: "100%" }}>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {request.reserveBy} Request to Change
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Equipment Name: {request.requestedEquipmentName}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Requested by: {request.reserveBy}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Start: {new Date(request.requestedSelectedStartDate).toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    End: {new Date(request.requestedSelectedEndDate).toLocaleString()}
                                                </Typography>
                                                {/* <Typography variant="body2" color="textSecondary">
                                                    No of Persons: {request.requestedChanges?.noOfPersons}
                                                </Typography> */}
                                                <Typography variant="body2" color="textSecondary">
                                                    Purpose of Equipment: {request.requestedPurposeOfEquipment}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => handleChangeEquipmentRequest(request.equipmentReservationId, "approved")}
                                                    disabled={loading}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleChangeEquipmentRequest(request.equipmentReservationId, "rejected")}
                                                    disabled={loading}
                                                >
                                                    Reject
                                                </Button>
                                            </Box>
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>
                                        <ListItemText primary="No pending requests." />
                                    </ListItem>
                                )
                            )}
                        </List>
                    </DialogContent>
                </Dialog>
            </IconContext.Provider>

            <Snackbar open={openSnackbar} autoHideDuration={9000} onClose={() => setOpenSnackbar(false)}>
                <Alert onClose={() => setOpenSnackbar(false)} severity="info" sx={{ width: "100%" }}>
                    Change request approved, reservation updated.
                </Alert>
            </Snackbar>

            <ToastContainer />
        </>
    );
};

export default Sidebar;